import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../axiosConfig';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import '../styles.css';
import LeavePolicyAssignCard from './LeavePolicyAssignCard';


const LeavesAdmin = () => {
  const [userData, setUserData] = useState(null);
  const [leaves, setLeaves] = useState([]);

  const [form, setForm] = useState({
    name: '',
    code: '',
    monthly_rate: 0,
    yearly_rate: 0,
    carry_forward: true,
  });

  const [editingLeaveId, setEditingLeaveId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Holiday modal state
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidayForm, setHolidayForm] = useState({
    title: '',
    date: '',
    description: '',
  });
  const [holidaySubmitting, setHolidaySubmitting] = useState(false);
  const [holidayError, setHolidayError] = useState('');
  const [holidaySuccess, setHolidaySuccess] = useState('');
  const [holidayResult, setHolidayResult] = useState(null);


  const currentUserId = localStorage.getItem('id');
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [roleError, setRoleError] = useState('');

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  useEffect(() => {
    if (!currentUserId) return;
    axiosInstance
      .get(`/user/${currentUserId}/`)
      .then((res) => setUserData(res.data))
      .catch((err) => {
        setRoleError(err.response?.data?.detail || err.message || 'Failed to load user');
      });
  }, [currentUserId]);

  const fetchLeaves = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosInstance.get('/leaves/');
      setLeaves(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch leaves');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleClick = () => setIsSidebarVisible((v) => !v);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const payload = useMemo(() => ({
    name: form.name.trim(),
    code: form.code.trim(),
    monthly_rate: Number(form.monthly_rate) || 0,
    yearly_rate: Number(form.yearly_rate) || 0,
    carry_forward: Boolean(form.carry_forward),
  }), [form]);

  const handleCreate = async () => {
    setSubmitting(true);
    setError('');
    setSuccess('');

    if (!payload.name) {
      setError('name is required');
      setSubmitting(false);
      return;
    }
    if (!payload.code) {
      setError('code is required');
      setSubmitting(false);
      return;
    }

    try {
      await axiosInstance.post('/leaves/', payload);
      setSuccess('Leave type created successfully');
      setForm({ name: '', code: '', monthly_rate: 0, yearly_rate: 0, carry_forward: true });
      await fetchLeaves();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data || err.message || 'Failed to create leave');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (lt) => {
    setEditingLeaveId(lt.id);

    setForm({
      name: lt.name || '',
      code: lt.code || '',
      monthly_rate: Number(lt.monthly_rate || 0),
      yearly_rate: Number(lt.yearly_rate || 0),
      carry_forward: Boolean(lt.carry_forward),
    });
    setError('');
    setSuccess('');
  };

  const handleUpdate = async () => {
    if (!editingLeaveId) return;

    const updatePayload = payload;
    setError('');
    setSuccess('');

    if (!payload.name) {
      setError('name is required');
      setSubmitting(false);
      return;
    }
    if (!payload.code) {
      setError('code is required');
      setSubmitting(false);
      return;
    }

    try {
      await axiosInstance.patch(`/leaves/${editingLeaveId}/`, payload);
      setSuccess('Leave type updated successfully');
      setEditingLeaveId(null);
      setForm({ name: '', code: '', monthly_rate: 0, yearly_rate: 0, carry_forward: true });
      await fetchLeaves();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data || err.message || 'Failed to update leave');
    } finally {
      setSubmitting(false);
    }
  };


  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  // Holiday handlers
  const handleHolidayFormChange = (e) => {
    const { name, value } = e.target;
    setHolidayForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateHoliday = async () => {
    setHolidaySubmitting(true);
    setHolidayError('');
    setHolidaySuccess('');
    setHolidayResult(null);

    if (!holidayForm.title.trim()) {
      setHolidayError('Title is required');
      setHolidaySubmitting(false);
      return;
    }

    if (!holidayForm.date) {
      setHolidayError('Date is required');
      setHolidaySubmitting(false);
      return;
    }

    try {
      const response = await axiosInstance.post('/holidays/', holidayForm);
      setHolidaySuccess('Holiday created successfully');
      setHolidayResult(response.data);
      setHolidayForm({ title: '', date: '', description: '' });
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowHolidayModal(false);
        setHolidayResult(null);
      }, 2000);
    } catch (err) {
      setHolidayError(err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to create holiday');
    } finally {
      setHolidaySubmitting(false);
    }
  };

  const handleOpenHolidayModal = () => {
    setShowHolidayModal(true);
    setHolidayError('');
    setHolidaySuccess('');
    setHolidayResult(null);
    setHolidayForm({ title: '', date: '', description: '' });
  };

  const handleCloseHolidayModal = () => {
    setShowHolidayModal(false);
    setHolidayForm({ title: '', date: '', description: '' });
    setHolidayError('');
    setHolidaySuccess('');
    setHolidayResult(null);
  };

  const menuItems = [
    { icon: '&#127968;', label: 'Dashboard', path: '/admin-manager-lead-dashboard' },
    { icon: '&#128196;', label: 'AR Requests', path: '/all-requests' },
      { icon: '&#128178;', label: 'Payroll', path: 'https://hrms.teamhalt.com/' },
      { icon: '&#128101;', label: 'Employees', path: '/employees' },
      { icon: '&#127970;', label: 'Roles & Office', path: '/roles-office' },
      { icon: '&#128197;', label: 'Leaves Admin', path: '/leaves-admin' },
      { icon: '&#128197;', label: 'Attendance Admin', path: '/attendance-admin' },
  ];

  const roleName = userData?.role_name;
  const isAllowed = roleName === 'admin' || roleName === 'manager';

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
      <Sidebar
        isSidebarVisible={isSidebarVisible}
        currentUser={userData}
        activeRole={userData?.role?.name}
        error={roleError}
        handleLogout={handleLogout}
        menuItems={menuItems}
      />

      <main className="dashboard-main-content">
        <div className="dashboard-header-section">
          <h1>📋 Leave Admin</h1>
          <p>Create and view leave types</p>
          <button
            className="submit-leave-btn"
            onClick={handleOpenHolidayModal}
            style={{ marginTop: '16px' }}
          >
            🎉 Add Holiday
          </button>
        </div>

        {!isAllowed ? (

          <div className="dashboard-card">
            <div className="no-data-message">
              <p>You are not authorized to access this page.</p>
            </div>
          </div>
        ) : (
          <>
              <div className="dashboard-grid leaves-admin-2col">
                <div className="dashboard-card">

                  <div className="card-header">
                <span className="card-icon">➕</span>
                <h3>{editingLeaveId ? 'Update Leave Type' : 'Create Leave Type'}</h3>
              </div>
                <div className="leave-admin-form">


                  {error && (

                    <div className="leave-request-error" style={{ marginBottom: 12 }}>
                      <span className="error-icon">⚠️</span>
                      <span>{String(error)}</span>
                    </div>

                  )}
                  {success && (
                    <div className="leave-request-success" style={{ marginBottom: 12 }}>
                      <span className="success-icon">✓</span>
                      <span>{success}</span>
                    </div>

                  )}
                  <div className="leave-admin-rate-grid">
                    <div className="form-group">
                      <label htmlFor="leaveTypeName">Name</label>
                      <input
                        id="leaveTypeName"
                        name="name"
                        className="form-input"
                        placeholder="e.g., Sick Leave"
                        value={form.name}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="leaveTypeCode">Code</label>
                      <input
                        id="leaveTypeCode"
                        name="code"
                        className="form-input"
                        placeholder="e.g., SL"
                        value={form.code}
                        onChange={handleChange}
                      />
                      <small style={{ color: '#64748b' }}>Must be unique (backend constraint)</small>
                    </div>
                    </div>

                  <div className="leave-admin-rate-grid">
                    <div className="form-group">
                      <label htmlFor="monthlyRate">Monthly Rate</label>
                      <input
                        id="monthlyRate"
                        name="monthly_rate"
                        type="number"
                        step="0.01"
                        className="form-input"
                        value={form.monthly_rate}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="yearlyRate">Yearly Rate</label>
                      <input
                        id="yearlyRate"
                        name="yearly_rate"
                        type="number"
                        step="0.01"
                        className="form-input"
                        value={form.yearly_rate}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-group leave-admin-checkbox">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="carry_forward"
                        checked={Boolean(form.carry_forward)}
                        onChange={handleChange}
                      />
                      <span className="checkbox-custom" />
                      <span className="checkbox-text">Carry forward to next cycle</span>
                    </label>
                    <div className="leave-admin-checkbox-spacer" />
                  </div>


                  <div className="form-actions">
                    <button
                      type="button"
                      className="submit-leave-btn"
                      onClick={editingLeaveId ? handleUpdate : handleCreate}
                      disabled={submitting}
                    >
                      {submitting ? (editingLeaveId ? 'Updating...' : 'Creating...') : (editingLeaveId ? 'Update Leave Type' : 'Create Leave Type')}
                    </button>

                  </div>
                </div>
                </div>


                <div className="dashboard-card">
                  <LeavePolicyAssignCard
                    leaves={leaves}
                    loading={loading}
                    disabled={submitting}
                  />
                </div>

                <div className="dashboard-card full-width leave-admin-table-card">
                <div className="card-header">
                  <span className="card-icon">📋</span>
                  <h3>Existing Leave Types</h3>
                </div>
                <div className="card-body">
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: 24 }}>
                      <div className="loading-spinner" style={{ margin: '0 auto' }} />
                      <p>Loading...</p>
                    </div>
                  ) : error ? (
                    <div style={{ color: '#ef4444', padding: '8px 0' }}>{error}</div>
                  ) : (
                    <div className="table-scroll">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Code</th>
                            <th>Monthly Rate</th>
                            <th>Yearly Rate</th>
                            <th>Carry Forward</th>
                            <th>Action</th>
                          </tr>

                        </thead>
                        <tbody>
                          {leaves.length > 0 ? (
                            leaves.map((lt) => (
                              <tr key={lt.id}>
                                <td>{lt.name}</td>
                                <td>
                                  <span style={{ fontWeight: 700 }}>{lt.code}</span>
                                </td>
                                <td>{Number(lt.monthly_rate || 0)}</td>
                                <td>{Number(lt.yearly_rate || 0)}</td>
                                <td>
                                  <span
                                    className={`status-badge ${lt.carry_forward ? 'approved' : 'rejected'}`}
                                  >
                                    {lt.carry_forward ? 'Yes' : 'No'}
                                  </span>
                                </td>
                                <td style={{ width: 140 }}>
                                  <button
                                    type="button"
                                    className="leave-admin-action-btn"
                                    onClick={() => handleEditClick(lt)}
                                    disabled={submitting}
                                  >
                                    Edit
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (

                            <tr>
                              <td colSpan={5} className="empty-state">
                                No leave types found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Holiday Modal */}
        {showHolidayModal && (
          <div className="modal-overlay" onClick={handleCloseHolidayModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>🎉 Add Office Holiday</h3>
                <button className="modal-close-btn" onClick={handleCloseHolidayModal}>×</button>
              </div>
              <div className="modal-body">
                {holidayError && (
                  <div className="leave-request-error" style={{ marginBottom: 12 }}>
                    <span className="error-icon">⚠️</span>
                    <span>{String(holidayError)}</span>
                  </div>
                )}
                {holidaySuccess && (
                  <div className="leave-request-success" style={{ marginBottom: 12 }}>
                    <span className="success-icon">✓</span>
                    <span>{holidaySuccess}</span>
                  </div>
                )}
                {holidayResult && (
                  <div className="leave-request-success" style={{ marginBottom: 12, backgroundColor: '#dbeafe', color: '#1e40af' }}>
                    <span className="success-icon">📊</span>
                    <span>
                      Updated {holidayResult.attendance_update.updated_count} users' attendance to PRESENT
                    </span>
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="holidayTitle">Holiday Title</label>
                  <input
                    id="holidayTitle"
                    name="title"
                    className="form-input"
                    placeholder="e.g., Diwali"
                    value={holidayForm.title}
                    onChange={handleHolidayFormChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="holidayDate">Date</label>
                  <input
                    id="holidayDate"
                    name="date"
                    type="date"
                    className="form-input"
                    value={holidayForm.date}
                    onChange={handleHolidayFormChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="holidayDescription">Description (Optional)</label>
                  <textarea
                    id="holidayDescription"
                    name="description"
                    className="form-input"
                    placeholder="e.g., Festival celebration"
                    value={holidayForm.description}
                    onChange={handleHolidayFormChange}
                    rows={3}
                  />
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    className="submit-leave-btn"
                    onClick={handleCreateHoliday}
                    disabled={holidaySubmitting}
                  >
                    {holidaySubmitting ? 'Creating...' : 'Create Holiday'}
                  </button>
                  <button
                    type="button"
                    className="cancel-leave-btn"
                    onClick={handleCloseHolidayModal}
                    disabled={holidaySubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LeavesAdmin;

